/**
 * Variant Split Gallery Verification System
 * 
 * ARCHITECTURAL CHANGE:
 * Media ordering is now handled server-side in Liquid - variant featured media is slide index 0.
 * This system is ONLY for post-render verification and dynamic load handling.
 * It does NOT control initial visual state - Liquid already ensures correct first render.
 * 
 * PURPOSE:
 * - Verify correct slide is displayed (safety check for edge cases)
 * - Handle dynamic loads (search, filters, theme editor, AJAX)
 * - Correct any unexpected reverts (shouldn't happen, but safety net)
 * 
 * PERFORMANCE:
 * - Single IntersectionObserver for viewport-based processing
 * - Queue-based processing (one card per frame)
 * - Minimal overhead - only runs when needed
 * - Early exit if correct slide is already at index 0 (most common case)
 */

(function() {
  'use strict';

  // Enable/disable logging for debugging
  const DEBUG = false; // Set to false to disable logging
  const log = () => {};
  const logError = () => {};
  const logWarn = () => {};

  // Global registry to track all variant-split galleries
  if (!window.__VariantSplitRegistry) {
    window.__VariantSplitRegistry = {
      galleries: new Map(), // instanceId -> galleryEl
      state: new Map(),      // instanceId -> { status: 'pending'|'queued'|'done'|'failed', attempts: number }
      queue: [],             // Queue of galleries waiting to be initialized
      processing: false      // Flag to prevent concurrent processing
    };
  }

  const registry = window.__VariantSplitRegistry;

  /**
   * Safe requestIdleCallback with fallback
   */
  function requestIdleCallbackSafe(callback, options) {
    if (typeof requestIdleCallback !== 'undefined') {
      return requestIdleCallback(callback, options);
    }
    // Fallback to setTimeout with small delay
    return setTimeout(callback, options?.timeout || 1);
  }

  /**
   * Safe CSS.escape with fallback for numeric IDs
   * If ID is all digits, no escaping needed. Otherwise use CSS.escape if available.
   */
  function safeEscapeSelector(id) {
    const str = String(id).trim();
    // If all digits, no escaping needed
    if (/^\d+$/.test(str)) {
      return str;
    }
    // Use CSS.escape if available
    if (typeof CSS !== 'undefined' && CSS.escape) {
      return CSS.escape(str);
    }
    // Simple fallback: escape special chars (basic, not comprehensive)
    return str.replace(/[!"#$%&'()*+,.\/:;<=>?@[\\\]^`{|}~]/g, '\\$&');
  }

  /**
   * Tolerant target slide finding with multiple selector fallbacks
   * Tries: [data-media-id], [data-slide-id], [data-media], then iteration
   */
  function findTargetSlide(galleryEl, variantMediaId) {
    const id = String(variantMediaId).trim();
    if (!id) {
      logWarn('findTargetSlide: Empty variantMediaId');
      return null;
    }

    const slideshow = galleryEl.querySelector('slideshow-component');
    if (!slideshow) {
      logWarn('findTargetSlide: Slideshow not found');
      return null;
    }

    const allSlides = slideshow.querySelectorAll('slideshow-slide');
    if (allSlides.length === 0) {
      logWarn('findTargetSlide: No slides found');
      return null;
    }

    log(`findTargetSlide: Looking for variantMediaId "${id}" in ${allSlides.length} slides`);

    // Log all slide IDs for debugging
    const slideIds = Array.from(allSlides).map((slide, idx) => {
      const slideId = slide.getAttribute('slide-id') || 
                     slide.getAttribute('data-slide-id') ||
                     slide.getAttribute('data-media-id') ||
                     slide.getAttribute('data-media') ||
                     'NO_ID';
      return `[${idx}]: ${slideId}`;
    });
    log(`findTargetSlide: All slide IDs: ${slideIds.join(', ')}`);

    // Try selectors in order (only if ID is safe for selector)
    const isNumeric = /^\d+$/.test(id);
    if (isNumeric || (typeof CSS !== 'undefined' && CSS.escape)) {
      const escapedId = isNumeric ? id : safeEscapeSelector(id);
      
      // Try: [data-media-id="${id}"]
      let targetSlide = slideshow.querySelector(`slideshow-slide[data-media-id="${escapedId}"]`);
      if (targetSlide) {
        const foundIndex = Array.from(allSlides).indexOf(targetSlide);
        log(`findTargetSlide: Found via data-media-id selector at index ${foundIndex}`);
        return targetSlide;
      }
      
      // Try: [data-slide-id="${id}"]
      targetSlide = slideshow.querySelector(`slideshow-slide[data-slide-id="${escapedId}"]`);
      if (targetSlide) {
        const foundIndex = Array.from(allSlides).indexOf(targetSlide);
        log(`findTargetSlide: Found via data-slide-id selector at index ${foundIndex}`);
        return targetSlide;
      }
      
      // Try: [data-media="${id}"]
      targetSlide = slideshow.querySelector(`slideshow-slide[data-media="${escapedId}"]`);
      if (targetSlide) {
        const foundIndex = Array.from(allSlides).indexOf(targetSlide);
        log(`findTargetSlide: Found via data-media selector at index ${foundIndex}`);
        return targetSlide;
      }
    }

    // Fallback: Iterate through all slides and match by attribute
    // Try slide-id first (most common)
    for (let i = 0; i < allSlides.length; i++) {
      const slide = allSlides[i];
      const slideId = slide.getAttribute('slide-id');
      const dataSlideId = slide.getAttribute('data-slide-id');
      const dataMediaId = slide.getAttribute('data-media-id');
      const dataMedia = slide.getAttribute('data-media');
      
      if (slideId === id || dataSlideId === id || dataMediaId === id || dataMedia === id) {
        log(`findTargetSlide: Found match at index ${i} - slide-id: ${slideId}, data-slide-id: ${dataSlideId}, data-media-id: ${dataMediaId}, data-media: ${dataMedia}`);
        return slide;
      }
    }

    logWarn(`findTargetSlide: No match found for variantMediaId "${id}"`);
    return null;
  }

  /**
   * Waits for slideshow to be ready (has select method and refs)
   * Uses lightweight polling with DOM presence checks
   */
  function waitForSlideshowReady(slideshow, timeout = 1500) {
    return new Promise((resolve) => {
      const startTime = Date.now();
      
      const check = () => {
        // Safety: Check if slideshow is still in DOM
        if (!slideshow || !document.contains(slideshow)) {
          resolve(false);
          return;
        }
        
        if (slideshow && 
            typeof slideshow.select === 'function' && 
            slideshow.refs?.slides && 
            slideshow.refs.slides.length > 0) {
          resolve(true);
          return;
        }
        
        if (Date.now() - startTime > timeout) {
          resolve(false);
          return;
        }
        
        setTimeout(check, 50);
      };
      
      check();
    });
  }

  /**
   * Sets up a monitor to watch for slide reverts and correct them
   * Used for index 0 cases where slideshow might revert after initialization
   */
  function setupSlideMonitor(slideshow, variantMediaId, instanceId, duration = 3000) {
    if (!slideshow || typeof slideshow.select !== 'function') return;
    
    const startTime = Date.now();
    let correctionCount = 0;
    const maxCorrections = 5; // Increased from 3
    
    log(`setupSlideMonitor: Starting monitor for ${instanceId} (variant: ${variantMediaId}) for ${duration}ms`);
    
    const monitor = setInterval(() => {
      if (Date.now() - startTime > duration) {
        log(`setupSlideMonitor: Monitor completed for ${instanceId}`);
        clearInterval(monitor);
        return;
      }
      
      if (!document.contains(slideshow)) {
        log(`setupSlideMonitor: Slideshow removed from DOM for ${instanceId}`);
        clearInterval(monitor);
        return;
      }
      
      const currentSlide = slideshow.slides?.[slideshow.current];
      const currentSlideId = currentSlide?.getAttribute('slide-id') || 
                            currentSlide?.getAttribute('data-slide-id') ||
                            currentSlide?.getAttribute('data-media-id');
      
      if (currentSlideId !== variantMediaId && correctionCount < maxCorrections) {
        logWarn(`setupSlideMonitor: Slide reverted for ${instanceId}! Was ${variantMediaId}, now ${currentSlideId}. Correcting... (attempt ${correctionCount + 1})`);
        try {
          slideshow.select({ id: variantMediaId }, undefined, { animate: false });
          correctionCount++;
          
          // Verify after a delay
          setTimeout(() => {
            const verifySlide = slideshow.slides?.[slideshow.current];
            const verifySlideId = verifySlide?.getAttribute('slide-id') || 
                                 verifySlide?.getAttribute('data-slide-id') ||
                                 verifySlide?.getAttribute('data-media-id');
            if (verifySlideId === variantMediaId) {
              log(`setupSlideMonitor: Correction successful for ${instanceId}`);
            } else {
              logWarn(`setupSlideMonitor: Correction failed for ${instanceId} - still showing ${verifySlideId}`);
            }
          }, 50);
        } catch (e) {
          logWarn(`setupSlideMonitor: Error correcting slide:`, e);
          if (correctionCount >= maxCorrections - 1) {
            clearInterval(monitor);
          }
        }
      } else if (currentSlideId === variantMediaId && correctionCount > 0) {
        log(`setupSlideMonitor: Slide corrected and stable for ${instanceId}`);
        // Keep monitoring but reduce frequency
      }
    }, 100); // Check every 100ms
  }

  /**
   * Hardened reveal logic - only reveals the target slide
   */
  function revealTargetSlide(targetSlide) {
    if (!targetSlide) return;
    
    // Remove hidden attribute if present
    if (targetSlide.hasAttribute('hidden')) {
      targetSlide.removeAttribute('hidden');
    }
    
    // Set reveal attribute
    targetSlide.setAttribute('reveal', '');
    
    // Set aria-hidden="false" if aria-hidden exists
    if (targetSlide.hasAttribute('aria-hidden')) {
      targetSlide.setAttribute('aria-hidden', 'false');
    }
    
    // Force reflow to ensure attributes are processed
    targetSlide.offsetHeight;
  }

  /**
   * Verifies and corrects a single gallery (if needed)
   * Since Liquid now handles media ordering, this is primarily a safety check
   * and handles dynamic loads (search, filters, theme editor)
   */
  async function initOneGallery(galleryEl) {
    const instanceId = galleryEl.dataset.galleryInstanceId;
    if (!instanceId) {
      logWarn('initOneGallery: No instanceId', galleryEl);
      return false;
    }

    const state = registry.state.get(instanceId);
    if (!state || state.status === 'done') {
      log(`initOneGallery: Skipping ${instanceId} - status: ${state?.status || 'no state'}`);
      return false;
    }

    log(`initOneGallery: Starting verification for ${instanceId}, attempt ${state.attempts + 1}`);

    // Safety: Check if gallery is still in DOM
    if (!document.contains(galleryEl)) {
      logWarn(`initOneGallery: Gallery ${instanceId} not in DOM`);
      registry.state.set(instanceId, { status: 'failed', attempts: state.attempts + 1 });
      return false;
    }

    // Get variant media ID from gallery or parent li
    let variantMediaId = galleryEl.dataset.variantMediaId;
    if (!variantMediaId) {
      const listItem = galleryEl.closest('li[data-variant-media-id]');
      variantMediaId = listItem?.dataset.variantMediaId;
      if (variantMediaId) {
        galleryEl.dataset.variantMediaId = variantMediaId;
      }
    }

    // Normalize variant media ID
    variantMediaId = String(variantMediaId).trim();
    if (!variantMediaId) {
      logWarn(`initOneGallery: No variantMediaId for ${instanceId}`);
      registry.state.set(instanceId, { status: 'failed', attempts: state.attempts + 1 });
      return false;
    }

    log(`initOneGallery: Variant media ID: ${variantMediaId}`);

    // Find slideshow component
    const slideshow = galleryEl.querySelector('slideshow-component');
    if (!slideshow) {
      // Not ready yet, will retry
      return false;
    }

    // Safety: Check if slideshow is still in DOM
    if (!document.contains(slideshow)) {
      registry.state.set(instanceId, { status: 'failed', attempts: state.attempts + 1 });
      return false;
    }

    // Wait for slideshow to be ready
    log(`initOneGallery: Waiting for slideshow to be ready...`);
    const isReady = await waitForSlideshowReady(slideshow);
    if (!isReady) {
      logWarn(`initOneGallery: Slideshow not ready within timeout`);
      return false;
    }

    log(`initOneGallery: Slideshow is ready`);

    // Safety: Final DOM check
    if (!document.contains(slideshow) || typeof slideshow.select !== 'function') {
      logWarn(`initOneGallery: Slideshow not in DOM or select() not available`);
      return false;
    }

    // VERIFICATION: Check if correct slide is already displayed
    // Since Liquid reordered media, slide index 0 should be the variant media
    const currentSlide = slideshow.slides?.[slideshow.current];
    const currentSlideId = currentSlide?.getAttribute('slide-id') || 
                          currentSlide?.getAttribute('data-slide-id') ||
                          currentSlide?.getAttribute('data-media-id');
    
    log(`initOneGallery: Current slide ID: ${currentSlideId}, target: ${variantMediaId}, current index: ${slideshow.current}`);
    
    // If correct slide is already displayed, mark as done
    if (currentSlideId === variantMediaId) {
      log(`initOneGallery: SUCCESS - Correct slide already displayed for ${instanceId}`);
      registry.state.set(instanceId, { status: 'done', attempts: state.attempts + 1 });
      return true;
    }

    // CORRECTION: Only correct if there's a mismatch (shouldn't happen for initial render, but handles dynamic loads)
    logWarn(`initOneGallery: Slide mismatch detected! Current: ${currentSlideId}, Target: ${variantMediaId}. Correcting...`);
    
    try {
      // Try to select by ID (most reliable)
      slideshow.select({ id: variantMediaId }, undefined, { animate: false });
      
      // Verify after a short delay
      await new Promise(resolve => setTimeout(resolve, 50));
      
      const verifySlide = slideshow.slides?.[slideshow.current];
      const verifySlideId = verifySlide?.getAttribute('slide-id') || 
                           verifySlide?.getAttribute('data-slide-id') ||
                           verifySlide?.getAttribute('data-media-id');
      
      if (verifySlideId === variantMediaId) {
        log(`initOneGallery: SUCCESS - Corrected slide for ${instanceId}`);
        registry.state.set(instanceId, { status: 'done', attempts: state.attempts + 1 });
        return true;
      } else {
        logWarn(`initOneGallery: Correction failed for ${instanceId} - still showing ${verifySlideId}`);
        return false; // Will retry
      }
    } catch (e) {
      // Ignore AbortError and ViewTransition errors
      const errorMsg = e.message || '';
      const errorName = e.name || '';
      
      if (errorName === 'AbortError' || 
          /viewtransition/i.test(errorMsg) ||
          /skipping.*transition/i.test(errorMsg)) {
        registry.state.set(instanceId, { status: 'done', attempts: state.attempts + 1 });
        return true;
      }
      
      logError(`initOneGallery: Error correcting slide for ${instanceId}:`, e);
      return false;
    }
  }

  /**
   * Processes one gallery from the queue per animation frame
   * This ensures we don't block the main thread
   */
  function processQueue() {
    if (registry.processing || registry.queue.length === 0) {
      return;
    }

    registry.processing = true;

    requestAnimationFrame(() => {
      const galleryEl = registry.queue.shift();
      if (!galleryEl) {
        registry.processing = false;
        return;
      }

      const instanceId = galleryEl.dataset.galleryInstanceId;
      if (!instanceId) {
        registry.processing = false;
        processQueue(); // Process next item
        return;
      }

      const state = registry.state.get(instanceId);

      // Skip if already done or too many attempts
      if (!state || state.status === 'done' || state.attempts >= 5) {
        registry.processing = false;
        processQueue(); // Process next item
        return;
      }

      // Update state to processing (from queued)
      if (state.status === 'queued') {
        state.status = 'pending';
      }

      // Try to initialize
      initOneGallery(galleryEl).then(success => {
        registry.processing = false;
        
        // If failed and attempts < max, re-enqueue for retry
        if (!success && state.attempts < 5) {
          requestIdleCallbackSafe(() => {
            // Only re-enqueue if not already in queue
            if (!registry.queue.includes(galleryEl)) {
              registry.queue.push(galleryEl);
              // Re-observe if not already observing
              if (!registry.galleries.has(instanceId)) {
                registry.galleries.set(instanceId, galleryEl);
                intersectionObserver.observe(galleryEl);
              }
            }
            processQueue();
          }, { timeout: 100 });
        } else {
          // Continue processing queue
          processQueue();
        }
      });
    });
  }

  /**
   * IntersectionObserver callback - adds galleries to queue when near viewport
   * HARDENED: Immediately unobserve and mark as queued to prevent duplicates
   */
  function handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const galleryEl = entry.target;
        const instanceId = galleryEl.dataset.galleryInstanceId;
        
        if (!instanceId) return;

        const state = registry.state.get(instanceId);
        
        // Skip if already done
        if (!state || state.status === 'done') {
          intersectionObserver.unobserve(galleryEl);
          return;
        }

        // Immediately unobserve to prevent duplicate triggers
        intersectionObserver.unobserve(galleryEl);

        // Mark as queued
        if (state.status === 'pending') {
          state.status = 'queued';
        }

        // Add to queue if not already there (duplicate prevention)
        if (!registry.queue.includes(galleryEl)) {
          registry.queue.push(galleryEl);
          processQueue();
        }
      }
    });
  }

  /**
   * Single IntersectionObserver for all galleries
   * rootMargin: '300px 0px' to initialize cards slightly before they enter viewport
   */
  const intersectionObserver = new IntersectionObserver(handleIntersection, {
    rootMargin: '300px 0px',
    threshold: 0.01
  });

  /**
   * Global function to register a gallery for observation
   * Called by inline scripts in card-gallery.liquid
   */
  window.__ObserveVariantSplitGallery = function(galleryEl) {
    if (!galleryEl || !galleryEl.classList.contains('card-gallery')) {
      logWarn('__ObserveVariantSplitGallery: Invalid gallery element');
      return;
    }

    const instanceId = galleryEl.dataset.galleryInstanceId;
    log(`__ObserveVariantSplitGallery: Registering gallery ${instanceId || 'NEW'}`);
    if (!instanceId) {
      // Generate instance ID if not set
      const productId = galleryEl.dataset.productId || '';
      const variantMediaId = galleryEl.dataset.variantMediaId || 
                            galleryEl.closest('li[data-variant-media-id]')?.dataset.variantMediaId || '';
      if (!variantMediaId) return; // No variant media ID, skip
      
      const uniqueId = `${productId}-${variantMediaId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      galleryEl.dataset.galleryInstanceId = uniqueId;
      galleryEl.dataset.variantMediaId = variantMediaId;
    }

    const finalInstanceId = galleryEl.dataset.galleryInstanceId;
    
    // Skip if already registered and done
    if (registry.galleries.has(finalInstanceId)) {
      const existingState = registry.state.get(finalInstanceId);
      if (existingState && existingState.status === 'done') {
        return; // Already done, skip
      }
    }
    
    // Register in maps
    registry.galleries.set(finalInstanceId, galleryEl);
    if (!registry.state.has(finalInstanceId)) {
      registry.state.set(finalInstanceId, { status: 'pending', attempts: 0 });
    }

    // CRITICAL: If gallery is already visible (above fold), initialize immediately
    // Don't wait for IntersectionObserver - this ensures variant images show right away
    const rect = galleryEl.getBoundingClientRect();
    const isVisible = rect.top < window.innerHeight + 300 && rect.bottom > -300;
    
    log(`__ObserveVariantSplitGallery: Gallery ${finalInstanceId} isVisible: ${isVisible}`);
    
    if (isVisible) {
      // Add to queue immediately for visible cards
      if (!registry.queue.includes(galleryEl)) {
        log(`__ObserveVariantSplitGallery: Adding ${finalInstanceId} to queue immediately`);
        registry.queue.push(galleryEl);
        processQueue();
      }
    } else {
      // Start observing for cards not yet visible
      log(`__ObserveVariantSplitGallery: Observing ${finalInstanceId} with IntersectionObserver`);
      intersectionObserver.observe(galleryEl);
    }
  };

  /**
   * Scan and register galleries in a given container
   * Used for late-load robustness (boot scan + shopify:section:load)
   */
  function scanAndRegisterGalleries(container = document) {
    const galleries = container.querySelectorAll('.card-gallery[data-gallery-instance-id]');
    galleries.forEach(gallery => {
      const instanceId = gallery.dataset.galleryInstanceId;
      if (!instanceId) return;
      
      // Only register if not already registered or if not done
      if (!registry.galleries.has(instanceId)) {
        window.__ObserveVariantSplitGallery(gallery);
      } else {
        const state = registry.state.get(instanceId);
        // Re-observe if failed and attempts remain
        if (state && state.status === 'failed' && state.attempts < 5) {
          state.status = 'pending';
          state.attempts = 0; // Reset attempts for retry
          intersectionObserver.observe(gallery);
        }
      }
    });
  }

  // Initialize galleries that are already in DOM (for SSR/hydration)
  // This catches galleries that were rendered before the script loaded
  function initializeExistingGalleries() {
    scanAndRegisterGalleries();
  }

  // Late-load robustness: Scan on boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeExistingGalleries);
  } else {
    // DOM already loaded
    initializeExistingGalleries();
  }

  // Late-load robustness: Listen for Shopify section loads
  // This handles dynamic section loading in theme editor and AJAX navigation
  document.addEventListener('shopify:section:load', function(event) {
    if (event.target && event.target.nodeType === 1) {
      scanAndRegisterGalleries(event.target);
    }
  });

  // Cleanup: unobserve galleries that are done
  setInterval(() => {
    registry.state.forEach((state, instanceId) => {
      if (state.status === 'done') {
        const galleryEl = registry.galleries.get(instanceId);
        if (galleryEl && document.contains(galleryEl)) {
          intersectionObserver.unobserve(galleryEl);
        }
      }
    });
  }, 5000); // Cleanup every 5 seconds

})();
